/*
 * Copyright (c) 2024-2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {useEffect, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {CalendarDaysIcon, PencilIcon, TrashIcon} from "@heroicons/react/24/solid";
import Dates from "utils/Dates";
import {useTypedSelector} from "helpers/reducers";
import {currencyNumberFormat, getNewDateObject, padMinutes} from "utils/utility";
import {useDispatch} from "useDispatch";
import BookingLoginAction, {ReservationsTableRequest} from "store/bookingLogin/BookingLoginAction";
import {getShortNameFromId} from "utils/reservationUtils";
import {toast} from "react-toastify";
import {Link} from "react-router-dom";
import clsx from "clsx";
import {useTable} from "utils/hooks/useTable";
import {ColumnFilter, ColumnFiltersState} from "@tanstack/react-table";
import BookingSellerReservationsTable from "pages/Front/Booking/Seller/BookingSellerReservationsTable";
import {
    BanknotesIcon,
    BuildingOffice2Icon,
    ClockIcon,
    CubeTransparentIcon,
    TagIcon,
    UserIcon,
    UsersIcon,
    WrenchScrewdriverIcon,
} from "@heroicons/react/24/outline";
import UiSearch from "pages/Front/components/Ui/components/UiSearch";
import AlertConfirmation from "pages/Front/Registration/components/AlertConfirmation";
import SellerAction from "store/seller/SellerAction";
import UiTabSwitcher, {UiTabSwitcherItem} from "pages/Front/components/Ui/components/UiTabSwitcher";
import UiDayMoving from "pages/Front/components/Ui/components/UiDayMoving";
import {ReservationOfferStatus} from "types/enums/ReservationOfferStatus";
import {CalendarView} from "types/enums/CalendarView";
import {Reservation} from "types/entities/Reservation";
import {getFirstDayOfWeekByDirection, getStartAndEndOfWeek} from "helpers/helpers";
import UiDateRangePicker from "pages/Front/components/Ui/components/UiDateRangePicker";
import {DateRange} from "types/entities/DateRange";
import UiDatePicker from "pages/Front/components/Ui/components/UiDatePicker";
import {useHistory} from "react-router";

export enum ReservationsFilterViewType {
    Day = "day",
    All = "all",
    Future = "future",
    Week = "week",
}

interface BookingSellerReservationsProps {
}

const BookingSellerReservations = (props: BookingSellerReservationsProps) => {
    const dispatch = useDispatch();
    const {t} = useTranslation();
    const history = useHistory();
    const seller_id = useTypedSelector(state => state.sessionState.session?.uid) || 1;
    const sellerData = useTypedSelector(state => state.seller);
    const tableState = sellerData.tableState;
    const reservations = sellerData.reservations || [];
    const {startOfWeek, endOfWeek} = getStartAndEndOfWeek(new Date());
    const [isTableStateSame, setIsTableStateSame] = useState<boolean>(false);

    // defaultne view rezervacii (All/Feature)
    const defaultReservationsFilterViewType = ReservationsFilterViewType.All;

    // origin default status
    const originReservationFilterStatus = ReservationOfferStatus.All;

    // const [viewType, setViewType] = useState<ReservationsFilterViewType>((filterViewType as ReservationsFilterViewType) || defaultReservationsFilterViewType);
    const [viewType, setViewType] = useState<ReservationsFilterViewType>(ReservationsFilterViewType.Week);

    // filter - reservation state
    const filterStatus: UiTabSwitcherItem[] = [
        {id: ReservationOfferStatus.All, name: t("All")},
        {id: ReservationOfferStatus.Lead, name: t("Lead")},
        {id: ReservationOfferStatus.New, name: t("New")},
        {id: ReservationOfferStatus.Sent, name: t("Sent")},
        {id: ReservationOfferStatus.Accepted, name: t("Accepted")},
        {id: ReservationOfferStatus.Finished, name: t("Finished")},
        {id: ReservationOfferStatus.Declined, name: t("Declined")},
    ];

    // filter - view all or day
    const filterDisplay: UiTabSwitcherItem[] = [
        {id: ReservationsFilterViewType.All, name: t("All")},
        // TODO: ak bude pristupne aj future, potom zakomponovat, treba zapracovat nejako aj filter do reduxu
        // {id: ReservationsFilterViewType.Future, name: t("Future")},
        {id: ReservationsFilterViewType.Week, name: t("Week")},
        {id: ReservationsFilterViewType.Day, name: t("Day")},
    ];

    // filter - change reservation status
    const onChangeFilterStatusHandler = (id: ReservationOfferStatus) => {
        const itemIndex = tableInit.columnFilters.findIndex(item => item.id === "offer");

        if (itemIndex !== -1) {
            if (id !== originReservationFilterStatus) {
                const newFilter = [...tableInit.columnFilters];
                newFilter[itemIndex].value = id;
                tableInit.setColumnFilters(newFilter);
            } else {
                tableInit.setColumnFilters(prevState => {
                    return prevState.filter(item => item.id !== "offer");
                });
            }
        } else {
            if (id !== originReservationFilterStatus) {
                tableInit.setColumnFilters([...tableInit.columnFilters, {id: "offer", value: id}]);
            }
        }
    };

    // view all/day
    const onClickActiveViewTypeHandler = (tab: ReservationsFilterViewType) => {
        setViewType(tab);
        if (tab === ReservationsFilterViewType.Day) {
            const itemIndex = tableInit.columnFilters.findIndex(item => item.id === "date");
            if (itemIndex === -1) {
                tableInit.setColumnFilters([
                    ...tableInit.columnFilters.filter(item => item.id !== "week"),
                    {
                        id: "date",
                        value: Dates.date(new Date(), "", "YYYY-MM-DD"),
                    },
                ]);
            } else {
                const newFilter = [...tableInit.columnFilters.filter(item => item.id !== "week")];
                newFilter[itemIndex].value = Dates.date(new Date(), "", "YYYY-MM-DD");
                tableInit.setColumnFilters(newFilter);
            }
        } else if (tab === ReservationsFilterViewType.Week) {
            const itemIndex = tableInit.columnFilters.findIndex(item => item.id === "week");
            if (itemIndex === -1) {
                tableInit.setColumnFilters([
                    ...tableInit.columnFilters.filter(item => item.id !== "date"),
                    {
                        id: "week",
                        value: [startOfWeek, endOfWeek],
                    },
                ]);
            } else {
                const newFilter = [...tableInit.columnFilters.filter(item => item.id !== "date")];
                newFilter[itemIndex].value = [startOfWeek, endOfWeek];
                tableInit.setColumnFilters(newFilter);
            }
        } else {
            tableInit.setColumnFilters(prevState => prevState.filter(item => item.id !== "date" && item.id !== "week"));
        }
    };

    // day moving
    const onNavigateHandler = (action: "NEXT" | "PREV" | "TODAY"): void => {
        if (viewType === ReservationsFilterViewType.Week) {
            movingWeek(action);
        } else {
            movingDay(action);
        }
    };

    const movingDay = (action: "NEXT" | "PREV" | "TODAY") => {
        const today = new Date();
        const filterDate = tableInit.columnFilters.find(item => item.id === "date");
        const date = filterDate && filterDate.value ? new Date(filterDate.value as string) : today;

        let newDate;
        switch (action) {
            case "NEXT":
                newDate = getNewDateObject(date, CalendarView.Day, 1);
                break;
            case "PREV":
                newDate = getNewDateObject(date, CalendarView.Day, -1);
                break;
            case "TODAY":
                newDate = getNewDateObject(today, CalendarView.Day, 0);
                break;
            default:
                newDate = getNewDateObject(today, CalendarView.Day, 0);
                break;
        }

        setViewType(ReservationsFilterViewType.Day);
        const itemIndex = tableInit.columnFilters.findIndex(item => item.id === "date");
        if (itemIndex !== -1) {
            const newFilter = [...tableInit.columnFilters.filter(item => item.id !== "week")];
            newFilter[itemIndex].value = newDate.year + "-" + newDate.month + "-" + newDate.day;
            tableInit.setColumnFilters(newFilter);
        } else {
            tableInit.setColumnFilters([
                ...tableInit.columnFilters.filter(item => item.id !== "week"),
                {
                    id: "date",
                    value: newDate.year + "-" + newDate.month + "-" + newDate.day,
                },
            ]);
        }
    }

    const movingWeek = (action: "NEXT" | "PREV" | "TODAY") => {
        const currentWeek = getStartAndEndOfWeek(new Date());
        const filterWeek = tableInit.columnFilters.find(item => item.id === "week");
        const date = (filterWeek && filterWeek.value) ? filterWeek.value[0] : currentWeek.startOfWeek;
        const newDates = getWeekDatesByAction(date, action);

        const itemIndex = tableInit.columnFilters.findIndex(item => item.id === "week");
        if (itemIndex !== -1) {
            const newFilter = [...tableInit.columnFilters.filter(item => item.id !== "date")];
            newFilter[itemIndex].value = [newDates.startOfWeek, newDates.endOfWeek];
            tableInit.setColumnFilters(newFilter);
        } else {
            tableInit.setColumnFilters([
                ...tableInit.columnFilters.filter(item => item.id !== "date"),
                {
                    id: "week",
                    value: [newDates.startOfWeek, newDates.endOfWeek],
                },
            ]);
        }
    }

    const getWeekDatesByAction = (date, action: "NEXT" | "PREV" | "TODAY") => {
        const currentDate = new Date();
        switch (action) {
            case "NEXT":
                const nextData = getFirstDayOfWeekByDirection(date, "next");
                return getStartAndEndOfWeek(nextData);
            case "PREV":
                const prevData = getFirstDayOfWeekByDirection(date, "previous");
                return getStartAndEndOfWeek(prevData);
            case "TODAY":
                return getStartAndEndOfWeek(currentDate);
            default:
                return getStartAndEndOfWeek(currentDate);
        }
    }

    const weekDateValue = (): DateRange | null => {
        const weekRange = tableInit.getSelectedValueFilter("week");
        return weekRange ? {from: weekRange[0], to: weekRange[1]} : null;
    }

    const onChangeWeekDateRangeHandler = (newDateRange: DateRange | null) => {
        tableInit.onChangeFilterHandler("week", newDateRange ? [Dates.ymd(newDateRange.from), Dates.ymd(newDateRange.to)] : null);
    }

    // global date picker
    const datePickerHandler = (date: Date | null): void => {
        const itemIndex = tableInit.columnFilters.findIndex(item => item.id === "date");
        const newDate = date ? Dates.date(new Date(date), "", "YYYY-MM-DD") : null;

        setViewType(date ? ReservationsFilterViewType.Day : defaultReservationsFilterViewType);
        if (itemIndex !== -1) {
            const newFilter = [...tableInit.columnFilters];
            newFilter[itemIndex].value = date !== null ? newDate : date;
            tableInit.setColumnFilters(newFilter);
        } else {
            tableInit.setColumnFilters([
                ...tableInit.columnFilters,
                {
                    id: "date",
                    value: date !== null ? newDate : date,
                },
            ]);
        }
    };

    const onDeleteItem = (reservationId: number) => {
        dispatch(BookingLoginAction.fetchDeleteReservationOffer(reservationId))
            .then(res => {
                toast.warning(t("Offer deleted successfully"));
            }).catch(err => {
                if (err?.status && err?.status === 401) {
                    history.replace("/booking/seller/login");
                }
            });
    };

    const onDeleteItemHandler = (reservationId: number) => {
        AlertConfirmation({
            message: t("Are you sure you want to do this?"),
            onClick: () => {
                onDeleteItem(reservationId);
            },
            title: t("Delete offer"),
        });
    };

    // table columns
    const columns = useMemo(
        () => [
            {
                accessorKey: "seller_id",
                enableColumnFilter: true,
                enableSorting: true,
                header: () => {
                    return (
                        <div className={"flex flex-row items-center"}>
                            <UserIcon className={"w-4 h-4 mr-1.5 flex-none"} />
                            <span className={"flex shrink-0 truncate"}>{t("Created by")}</span>
                        </div>
                    );
                },
                cell: ({row}) => {
                    const reservation = row.original;
                    return <span>{reservation?.seller?.name ? reservation.seller.name + " " + reservation.seller?.last_name : "---"}</span>;
                },
            },
            {
                accessorKey: "offer_number",
                enableColumnFilter: false,
                enableSorting: true,
                header: () => {
                    return (
                        <div className={"flex flex-row items-center"}>
                            <UserIcon className={"w-4 h-4 mr-1.5 flex-none"} />
                            <span className={"flex shrink-0 truncate"}>{t("Offer #")}</span>
                        </div>
                    );
                },
                cell: ({row}) => {
                    const reservation = row.original;
                    return <span>{reservation.offer_number}</span>;
                },
            },
            {
                accessorKey: "created_at",
                enableColumnFilter: false,
                enableSorting: true,
                header: () => {
                    return (
                        <div className={"flex flex-row items-center"}>
                            <CalendarDaysIcon className={"w-4 h-4 mr-1.5 flex-none"} />
                            <span className={"flex shrink-0 truncate"}>{t("Created at")}</span>
                        </div>
                    );
                },
                cell: ({row}) => {
                    const reservation = row.original;
                    return <span>{reservation.created_at && Dates.ymd(reservation.created_at, "DD.MM.YYYY HH:mm")}</span>;
                },
            },
            {
                accessorKey: "seller_edited_at",
                enableColumnFilter: false,
                enableSorting: true,
                header: () => {
                    return (
                        <div className={"flex flex-row items-center"}>
                            <CalendarDaysIcon className={"w-4 h-4 mr-1.5 flex-none"} />
                            <span className={"flex shrink-0 truncate"}>{t("Edited at")}</span>
                        </div>
                    );
                },
                cell: ({row}) => {
                    const reservation: Reservation = row.original;
                    return <span>{reservation.seller_edited_at && Dates.ymd(reservation.seller_edited_at, "DD.MM.YYYY HH:mm")}</span>;
                },
            },
            {
                accessorKey: "company_id",
                enableColumnFilter: true,
                enableSorting: true,
                header: () => {
                    return (
                        <div className={"flex flex-row items-center"}>
                            <BuildingOffice2Icon className={"w-4 h-4 mr-1.5 flex-none"} />
                            <span className={"flex shrink-0 truncate"}>{t("Company")}</span>
                        </div>
                    );
                },
                cell: ({row}) => {
                    const reservation = row.original;
                    return (
                        <div className={"font-medium"}>
                            {reservation.company?.name &&
                                <div className={"inline-flex w-full leading-tight"}>{reservation.company?.name}</div>}
                            {(reservation.company?.first_name || reservation.company?.last_name) && (
                                <div className={"flex w-full gap-x-1 leading-tight"}>
                                    {reservation.company?.first_name && <span>{reservation.company?.first_name}</span>}
                                    {reservation.company?.last_name && <span>{reservation.company?.last_name}</span>}
                                </div>
                            )}
                        </div>
                    );
                },
            },
            {
                accessorKey: "package",
                enableColumnFilter: false,
                enableSorting: false,
                header: () => {
                    return (
                        <div className={"flex flex-row items-center"}>
                            <CubeTransparentIcon className={"w-4 h-4 mr-1.5 flex-none"} />
                            <span className={"flex shrink-0 truncate"}>{t("Package")}</span>
                        </div>
                    );
                },
                cell: ({row}) => {
                    const reservation = row.original;
                    return <span>{getShortNameFromId(reservation.category ?? "---")}</span>;
                },
            },
            {
                accessorKey: "date",
                enableColumnFilter: false,
                enableSorting: true,
                header: () => {
                    return (
                        <div className={"flex flex-row items-center"}>
                            <CalendarDaysIcon className={"w-4 h-4 mr-1.5 flex-none"} />
                            <span className={"flex shrink-0 truncate"}>{t("Date")}</span>
                        </div>
                    );
                },
                cell: ({row}) => {
                    const reservation = row.original;
                    return <span>{Dates.ymd(reservation.date, "DD.MM.YYYY")}</span>;
                },
            },
            {
                accessorKey: "time",
                enableColumnFilter: false,
                enableSorting: false,
                header: () => {
                    return (
                        <div className={"flex flex-row items-center"}>
                            <ClockIcon className={"w-4 h-4 mr-1.5 flex-none"} />
                            <span className={"flex shrink-0 truncate"}>{t("Time")}</span>
                        </div>
                    );
                },
                cell: ({row}) => {
                    const reservation = row.original;
                    return (
                        <span>
                            {padMinutes(reservation.hour)}:{padMinutes(reservation.minute)}
                        </span>
                    );
                },
            },
            {
                accessorKey: "persons",
                enableColumnFilter: false,
                enableSorting: false,
                header: () => {
                    return (
                        <div className={"flex flex-row items-center justify-center w-full"}>
                            <UsersIcon className={"w-4 h-4 mr-1.5 flex-none"} />
                            <span className={"flex shrink-0 truncate"}>{t("Persons")}</span>
                        </div>
                    );
                },
                cell: ({row}) => {
                    const reservation = row.original;
                    return <div className={"text-center w-full"}>{reservation.persons}</div>;
                },
            },
            {
                accessorKey: "offer",
                enableColumnFilter: false,
                enableSorting: true,
                header: () => {
                    return (
                        <div className={"flex flex-row items-center"}>
                            <TagIcon className={"w-4 h-4 mr-1.5 flex-none"} />
                            <span className={"flex shrink-0 truncate"}>{t("Status")}</span>
                        </div>
                    );
                },
                cell: ({row}) => {
                    const reservation = row.original;
                    return <span>{t(reservation.offer)}</span>;
                },
            },
            {
                accessorKey: "total",
                enableColumnFilter: false,
                enableSorting: false,
                header: () => {
                    return (
                        <div className={"flex flex-row items-center"}>
                            <BanknotesIcon className={"w-4 h-4 mr-1.5 flex-none"} />
                            <span className={"flex shrink-0 truncate"}>{t("Total")}</span>
                        </div>
                    );
                },
                cell: ({row}) => {
                    const reservation = row.original;
                    return <span>{reservation.price ? currencyNumberFormat(reservation.price) : "---"}</span>;
                },
            },
            {
                accessorKey: "actions",
                enableColumnFilter: false,
                enableSorting: false,
                header: () => <WrenchScrewdriverIcon className={"w-4 h-4 mr-1.5 flex-none"} />,
                cell: ({row}) => {
                    const reservation = row.original;
                    return (
                        <div className={"w-full text-right flex flex-row gap-x-2 justify-end"}>
                            <button
                                className={"bg-red-500 hover:bg-red-600 text-white px-2 py-1.5 rounded-md"}
                                onClick={() => onDeleteItemHandler(reservation.id)}
                            >
                                <TrashIcon className={clsx("w-4 h-4 flex-none")} />
                            </button>
                            <Link
                                to={"/booking/seller/reservations/detail/" + reservation.id}
                                className={
                                    "rounded-md px-2 py-1.5 border border-blue-500 bg-blue-500 active:bg-opacity-80 focus:bg-opacity-80 hover:bg-opacity-90 text-white flex w-full md:w-auto items-center text-center justify-center leading-6 font-bold cursor-pointer"
                                }
                            >
                                <PencilIcon className={clsx("w-4 h-4 flex-none")} />
                            </Link>
                        </div>
                    );
                },
            },
        ],
        [],
    );

    // check table state, if the same
    const checkTableStateSame = (tableState: ReservationsTableRequest, newTableState: ReservationsTableRequest) => {
        const keys1 = tableState ? Object.keys(tableState) : [];
        const keys2 = newTableState ? Object.keys(newTableState) : [];

        if (keys1.length !== keys2.length) {
            return false;
        }

        for (const key of keys1) {
            if (tableState[key] !== newTableState[key]) {
                return false;
            }
        }
        return true;
    };

    const onChangeSearchHandler = (data: string | null) => {
        if (data == null) {
            dispatch(SellerAction.setTableState({
                ...tableState,
                search: tableState.search?.filter(item => item.id !== "all"),
            }));
        }
        tableInit.onChangeSearchHandler(data);
    };

    const getGlobalFilterFromAllSearch = (search: ColumnFiltersState): string => {
        const isSearch = search.find(item => item.id === "all");
        return isSearch ? isSearch.value as string : "";
    };

    // table init
    const tableInit = useTable({
        defaultData: reservations,
        columns: columns,
        enableRowSelection: true,
        defaultRecordsPage: (tableState?.page && tableState?.page != 0) ? tableState?.page - 1 : 0,
        defaultRecordsPerPage: tableState?.per_page || 20,
        defaultColumnFilters: tableState?.search ? [...tableState.search] : [],
        defaultGlobalFilter: (tableState?.search && tableState?.search?.length > 0) ? getGlobalFilterFromAllSearch(tableState.search) : "",
        defaultSorting: tableState?.sorting ? [...tableState.sorting] : [
            {id: "created_at", desc: true},
        ],
    });

    // fetch reservations data
    const fetchReservations = (resetPagination: boolean) => {
        if (tableInit.isLoading) {
            return;
        }
        tableInit.setIsLoading(true);

        const searchQuery: ColumnFiltersState = tableInit.globalFilter
            ? [...tableInit.columnFilters.filter(item => item.id !== "all"), {id: "all", value: tableInit.globalFilter}]
            : tableInit.columnFilters.length > 0
                ? tableInit.columnFilters.filter(item => item.id !== "all")
                : [];
        const resData: ReservationsTableRequest = {
            page: resetPagination ? 0 : tableInit.pageIndex + 1,
            per_page: tableInit.pageSize,
            search: searchQuery ? tableInit.clearNullableFilters(searchQuery) : [],
            sorting: tableInit.sorting.length > 0 ? tableInit.sorting : [],
        };

        // don't show loading if fetching the same table state (sort, pagination,...)
        setIsTableStateSame(checkTableStateSame(resData, tableState));

        dispatch(BookingLoginAction.fetchReservationsListForSellerWithPagination(seller_id, resData))
            .then(res => {
                dispatch(SellerAction.setReservations(res.data));
                dispatch(SellerAction.setTableState(resData));
                tableInit.setRecordsTable(res.data);
                tableInit.setPagination({pageIndex: res.current_page - 1, pageSize: res.per_page});
                tableInit.setPaginationTotal(Math.ceil(res.total / res.per_page));
                tableInit.setRecordsCount(res.total);

                // view typ, if set date, must set day, otherwise default
                if (resData?.search?.find(item => item.id === "date")) {
                    setViewType(ReservationsFilterViewType.Day);
                } else if (resData?.search?.find(item => item.id === "week")) {
                    setViewType(ReservationsFilterViewType.Week);
                } else {
                    setViewType(defaultReservationsFilterViewType);
                }
            })
            .catch(err => {
                toast.error(t("Could not load data"));
                if (err?.status && err?.status === 401) {
                    history.replace("/booking/seller/login");
                }
            })
            .finally(() => {
                tableInit.setIsLoading(false);
            });
    };

    // if array is the same result is false, otherwise return true
    const resetPaginationIfArraysAreDifferent = (array1: ColumnFilter[], array2: ColumnFilter[]) => {
        if (array1.length !== array2.length) {
            return true;
        }

        const map1 = new Map(array1.map((filter) => [filter.id, filter.value]));
        const map2 = new Map(array2.map((filter) => [filter.id, filter.value]));

        for (const [id, value] of map1) {
            if (!map2.has(id) || map2.get(id) !== value) {
                return true;
            }
        }

        return false; // if array is the same result is false
    };

    // ak sa zmeni filter/pagination/sorting nacitaj reservations data
    useEffect(() => {
        if ((tableInit.pageIndex && tableInit.pageSize) || tableInit.columnFilters || tableInit.sorting || tableInit.globalFilter) {
            const resetPagination = resetPaginationIfArraysAreDifferent(tableInit.lastColumnFilters, tableInit.columnFilters);
            tableInit.setLastColumnFilters(tableInit.columnFilters);
            fetchReservations(resetPagination);
        }
    }, [tableInit.pageIndex, tableInit.pageSize, tableInit.columnFilters, tableInit.sorting, tableInit.globalFilter, reservations]);

    useEffect(() => {
        tableInit.setLastColumnFilters(tableState?.search || []);
    }, []);

    return (
        <div className={"BookingSellerReservations w-full overflow-hidden"}>
            <div className={"w-full text-left flex flex-row justify-between mb-1"}>
                <h1 className={"text-gray-900 text-2xl font-bold mb-4"}>{t("Your offers")}</h1>
            </div>

            <div className={"w-full text-left flex flex-row justify-between mb-4 gap-4"}>
                <UiTabSwitcher
                    classNameButton={"capitalize"}
                    disabled={tableInit.isLoading}
                    items={filterStatus}
                    selected={tableInit.getSelectedValueFilter("offer", originReservationFilterStatus) as string}
                    onClick={id => onChangeFilterStatusHandler(id as ReservationOfferStatus)}
                />

                <div className={"flex flex-row items-center space-x-4"}>
                    <UiTabSwitcher
                        disabled={tableInit.isLoading}
                        items={filterDisplay}
                        selected={viewType}
                        onClick={tab => onClickActiveViewTypeHandler(tab as ReservationsFilterViewType)}
                    />
                    <UiDayMoving
                        disabled={tableInit.isLoading}
                        selected={tableInit.getSelectedValueFilter("date") as string | null}
                        onChange={onNavigateHandler}
                    />

                    {viewType === ReservationsFilterViewType.Week
                        ? (
                            <UiDateRangePicker
                                disabled={tableInit.isLoading}
                                selectedDay={weekDateValue()}
                                onChange={onChangeWeekDateRangeHandler}
                            />
                        ) : (
                            <UiDatePicker
                                disabled={tableInit.isLoading}
                                selectedDay={tableInit.getSelectedValueFilter("date") as string | null}
                                onChange={datePickerHandler}
                            />
                        )
                    }
                </div>

                <UiSearch
                    disabled={tableInit.isLoading}
                    placeholder={t("Write reservation id")}
                    value={tableInit.globalFilter}
                    onSubmit={data => onChangeSearchHandler(data.search)}
                />
            </div>

            <BookingSellerReservationsTable tableInit={tableInit} isTableStateSame={isTableStateSame} />
        </div>
    );
};

export default BookingSellerReservations;
